import { useState } from "react";
import { m as motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { fadeInUp, defaultTransition, defaultViewport } from "@/lib/motion";
import { useSEO } from "@/lib/use-seo";

export const ContactUs = () => {
  const [formState, setFormState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  useSEO({
    title: "Contact Us",
    description: "Get in touch with the AI for HR Mastermind team. Ask questions about the program, team training, or partnerships.",
    path: "/contact",
  });
  return (
    <div className="min-h-screen flex flex-col pt-32 md:pt-8">
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-30 pointer-events-none" />
        <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="section-inner relative z-10">
          <motion.div
            initial={fadeInUp.initial}
            whileInView={fadeInUp.animate}
            viewport={defaultViewport}
            transition={defaultTransition}
            className="text-center mb-10"
          >
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--secondary))] to-[hsl(var(--accent))]">
              Contact Us
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Have a question or want to learn more? We'd love to hear from you.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={defaultViewport}
            transition={{ ...defaultTransition, delay: 0.2 }}
            className="max-w-3xl mx-auto glass-card rounded-2xl overflow-hidden"
          >
            <noscript>
              <div className="p-8 text-center">
                <p className="text-base text-foreground">
                  The contact form needs JavaScript. Email us at{" "}
                  <a
                    href="mailto:hello@aiforhrmastermind.com"
                    className="font-semibold text-primary underline underline-offset-4"
                  >
                    hello@aiforhrmastermind.com
                  </a>{" "}
                  and we&apos;ll reply within one business day.
                </p>
              </div>
            </noscript>
            <div style={{ position: "relative", height: "80dvh", overflow: "auto" }}>
              {formState !== "ready" && (
                <div
                  className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-6 text-center bg-background/60"
                  aria-live="polite"
                >
                  {formState === "loading" ? (
                    <>
                      <Loader2
                        className="w-6 h-6 animate-spin motion-reduce:animate-none text-muted-foreground"
                        aria-hidden="true"
                      />
                      <p className="text-sm text-muted-foreground">
                        Loading the contact form&hellip;
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-base font-semibold text-foreground">
                        The contact form didn&apos;t load
                      </p>
                      <p className="text-sm text-muted-foreground max-w-sm">
                        An ad blocker or network issue may be blocking it. Email
                        us directly at{" "}
                        <a
                          href="mailto:hello@aiforhrmastermind.com"
                          className="font-semibold text-primary underline underline-offset-4 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                        >
                          hello@aiforhrmastermind.com
                        </a>
                        .
                      </p>
                    </>
                  )}
                </div>
              )}
              <iframe
                onLoad={() => setFormState("ready")}
                onError={() => setFormState("error")}
                src="https://app.formbricks.com/s/cmnnpilupdjlvsy01gsuattpx?embed=true"
                frameBorder="0"
                title="Contact Form"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  border: 0,
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
